import {
  ChartFrequency,
  IComposableChartsWidget,
  IPostgreJobsAnalyticsPayload,
} from 'wisestep-api';

import { ITimePeriod } from '../../../analyticsPage/helper/postgreDrawerChartsList';
import { StatusList } from '../../../leads/components/LeadDetailsForm';
import { getChartFreqForTimePeriod, LeadWidget } from '../../DerivedCharts/helper';
import { jobsTimePeriodList } from '../../DerivedCharts/Jobs';
import { ChartScope } from '../../types';

import LeadCountMetric from './LeadCountMetric';
import LeadCountTable from './LeadCountTable';
import LeadTrendBarChart from './LeadTrendBarChart';

const getLeadWidgetsByTimeAndScope = (
  timePeriodId: string,
  scope: ChartScope,
  frequency: ChartFrequency = getChartFreqForTimePeriod[timePeriodId]
): Partial<Record<LeadWidget, IComposableChartsWidget>> => {
  const defaultQuery: IPostgreJobsAnalyticsPayload = {
    category: 'lead',
    filters: [],
    type: 'lead-created-metric',
    range: timePeriodId,
    scope,
    frequency,
  };
  const freqInRegularCase = frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();

  const widgetConfig = {
    [LeadWidget.METRIC_CREATED]: {
      name: 'Leads Created',
      filters: {},
      category: 'lead',
      type: `metric_lead_created_${timePeriodId}_${scope}`,
      component: (
        <LeadCountMetric
          query={{ ...defaultQuery, type: 'lead-created-metric' }}
          title="Leads Created"
          widgetName={LeadWidget.METRIC_CREATED}
        />
      ),
    },
    [LeadWidget.METRIC_OWNED]: {
      name: 'Leads Owned',
      filters: {},
      category: 'lead',
      type: `metric_lead_owned_${timePeriodId}_${scope}`,
      component: (
        <LeadCountMetric
          query={{ ...defaultQuery, type: 'lead-owned-metric' }}
          title="Leads Owned"
          widgetName={LeadWidget.METRIC_OWNED}
        />
      ),
    },
    [LeadWidget.METRIC_CREATED_BY_STATUS]: {
      name: 'Leads Created by Status',
      filters: {},
      category: 'lead',
      type: `metric_lead_created_status_${timePeriodId}_${scope}`,
      component: (
        <LeadCountMetric
          query={{ ...defaultQuery, type: 'lead-created-status-wise-metric' }}
          title="Leads Created by Status"
          widgetName={LeadWidget.METRIC_CREATED_BY_STATUS}
        />
      ),
    },
    [LeadWidget.METRIC_OWNED_BY_STATUS]: {
      name: 'Leads Owned by Status',
      filters: {},
      category: 'lead',
      type: `metric_lead_owned_status_${timePeriodId}_${scope}`,
      component: (
        <LeadCountMetric
          query={{ ...defaultQuery, type: 'lead-owned-status-wise-metric' }}
          title="Leads Owned by Status"
          widgetName={LeadWidget.METRIC_OWNED_BY_STATUS}
        />
      ),
    },
    [LeadWidget.METRIC_UPDATED_BY_STATUS]: {
      name: 'Leads Updated by Status',
      filters: {},
      category: 'lead',
      type: `metric_lead_updated_status_${timePeriodId}_${scope}`,
      component: (
        <LeadCountMetric
          query={{
            ...defaultQuery,
            type: 'lead-updated-status-wise-metric',
            filterDate: 'modified_on',
          }}
          title="Leads Updated by Status"
          widgetName={LeadWidget.METRIC_UPDATED_BY_STATUS}
        />
      ),
    },
    [LeadWidget.TABLE_CREATED]: {
      name: `Leads Created by User`,
      filters: {},
      category: 'lead',
      type: `table_lead_created_all_user_${timePeriodId}_${scope}`,
      component: (
        <LeadCountTable
          query={{ ...defaultQuery, type: 'lead-created-table' }}
          title={`Leads Created by User`}
          widgetName={LeadWidget.TABLE_CREATED}
          widgetType={`table_lead_created_all_user_${timePeriodId}_${scope}`}
        />
      ),
    },
    [LeadWidget.TABLE_OWNED]: {
      name: `Leads Owned by User`,
      filters: {},
      category: 'lead',
      type: `table_lead_owned_all_user_${timePeriodId}_${scope}`,
      component: (
        <LeadCountTable
          query={{ ...defaultQuery, type: 'lead-owned-table' }}
          title={`Leads Owned by User`}
          widgetName={LeadWidget.TABLE_OWNED}
          widgetType={`table_lead_owned_all_user_${timePeriodId}_${scope}`}
        />
      ),
    },
    [LeadWidget.BAR_CREATED]: {
      name: `Leads Created - ${freqInRegularCase}`,
      filters: {},
      category: 'lead',
      type: `barchart_lead_created_${timePeriodId}_${scope}_${frequency}`,
      component: (
        <LeadTrendBarChart
          query={{ ...defaultQuery, type: 'lead-created-trend' }}
          title={`Leads Created - ${freqInRegularCase}`}
          widgetName={LeadWidget.BAR_CREATED}
        />
      ),
    },
    [LeadWidget.BAR_OWNED]: {
      name: `Leads Owned - ${freqInRegularCase}`,
      filters: {},
      category: 'lead',
      type: `barchart_lead_owned_${timePeriodId}_${scope}_${frequency}`,
      component: (
        <LeadTrendBarChart
          query={{ ...defaultQuery, type: 'lead-owned-trend' }}
          title={`Leads Owned - ${freqInRegularCase}`}
          widgetName={LeadWidget.BAR_OWNED}
        />
      ),
    },
  };

  return widgetConfig;
};

const getLeadWidgetsForStatusByTimeAndScope = (
  timePeriod: ITimePeriod,
  scope: ChartScope,
  leadStatus: StatusList,
  frequency: ChartFrequency = getChartFreqForTimePeriod[timePeriod.id]
): Partial<Record<LeadWidget, IComposableChartsWidget>> => {
  const timePeriodId = timePeriod.id;
  const filterDate = 'created_on';
  const leadStatusLabel = leadStatus.label;
  const filters = [
    {
      filterName: 'LEAD_STATUS',
      values: [leadStatus.value],
    },
  ];
  const defaultQuery: IPostgreJobsAnalyticsPayload = {
    category: 'lead',
    filters,
    type: 'lead-created-metric',
    range: timePeriodId,
    filterDate,
    scope,
    frequency,
  };

  const widgetConfig = {
    [LeadWidget.TABLE_CREATED_BY_STATUS]: {
      name: `Leads Created for Status '${leadStatusLabel}' by User`,
      filters: {},
      category: 'lead',
      type: `table_lead_created_user_${timePeriodId}_${scope}_${leadStatus.value}_${filterDate}`,
      component: (
        <LeadCountTable
          query={{ ...defaultQuery, type: 'lead-created-table' }}
          title={`Leads Created for Status '${leadStatusLabel}' by User`}
          widgetName={LeadWidget.TABLE_CREATED_BY_STATUS}
          leadStatusLabel={leadStatusLabel}
          widgetType={`table_lead_created_user_${timePeriodId}_${scope}_${leadStatus.value}_${filterDate}`}
        />
      ),
    },
    [LeadWidget.TABLE_OWNED_BY_STATUS]: {
      name: `Leads Owned for Status '${leadStatusLabel}' by User`,
      filters: {},
      category: 'lead',
      type: `table_lead_owned_user_${timePeriodId}_${scope}_${leadStatus.value}_${filterDate}`,
      component: (
        <LeadCountTable
          query={{ ...defaultQuery, type: 'lead-owned-table' }}
          title={`Leads Owned for Status '${leadStatusLabel}' by User`}
          widgetName={LeadWidget.TABLE_OWNED_BY_STATUS}
          leadStatusLabel={leadStatusLabel}
          widgetType={`table_lead_owned_user_${timePeriodId}_${scope}_${leadStatus.value}_${filterDate}`}
        />
      ),
    },
    [LeadWidget.TABLE_PIPELINE]: {
      name: `Leads Pipeline for Status '${leadStatusLabel}'`,
      filters: {},
      category: 'lead',
      type: `table_lead_pipeline_${timePeriodId}_${scope}_${leadStatus.value}_${filterDate}`,
      component: (
        <LeadCountTable
          query={{ ...defaultQuery, type: 'lead-pipeline-table' }}
          title={`Leads Pipeline for Status '${leadStatusLabel}'`}
          widgetName={LeadWidget.TABLE_PIPELINE}
          leadStatusLabel={leadStatusLabel}
          widgetType={`table_lead_pipeline_${timePeriodId}_${scope}_${leadStatus.value}_${filterDate}`}
        />
      ),
    },
  };

  return widgetConfig;
};

export const leadStaticChartsKeys = (
  timePeriodId: string,
  scope: ChartScope
): IComposableChartsWidget[] => {
  const defaultWidgetList = Object.values(getLeadWidgetsByTimeAndScope(timePeriodId, scope));

  return defaultWidgetList;
};

export const leadChartsByStatusKeys = (
  timePeriod: ITimePeriod,
  scope: ChartScope,
  leadStatusList: StatusList[] = []
): IComposableChartsWidget[] => {
  return leadStatusList.flatMap((leadStatus) =>
    Object.values(getLeadWidgetsForStatusByTimeAndScope(timePeriod, scope, leadStatus))
  );
};

export const leadDashboardKeysObjectList = jobsTimePeriodList.flatMap((timePeriod) => [
  ...leadStaticChartsKeys(timePeriod.id, ChartScope.WORKSPACE),
  ...leadStaticChartsKeys(timePeriod.id, ChartScope.LOGGED_IN_USER),
]);
